import { PlayArrow, Pause, Forward10, Replay10 } from '@mui/icons-material'
import { Typography, IconButton, Slider, Stack } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useGetTranscriptionQuery } from '@/core/services/transcription'
import { useGetPresignedUrlMutation } from '@/core/api/recording'

dayjs.extend(duration)

export function TranscriptionAudioPlayer({ transcriptionId }: { transcriptionId: string }) {
  const { data: transcription } = useGetTranscriptionQuery(transcriptionId)
  const [getPresignUrl] = useGetPresignedUrlMutation()
  const [presignedUrl, setPresignedUrl] = useState<string>('')
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [speed, setSpeed] = useState(1)
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (transcription?.audioKey) {
      ;(async () => {
        const url = await getPresignUrl({ key: transcription.audioKey! }).unwrap()
        setPresignedUrl(url)
      })()
    }
  }, [transcription])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const onTimeUpdate = () => setCurrentTime(audio.currentTime)
    const onLoaded = () => setDuration(audio.duration)
    const onEnded = () => setIsPlaying(false)

    audio.addEventListener('timeupdate', onTimeUpdate)
    audio.addEventListener('loadedmetadata', onLoaded)
    audio.addEventListener('ended', onEnded)

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate)
      audio.removeEventListener('loadedmetadata', onLoaded)
      audio.removeEventListener('ended', onEnded)
    }
  }, [presignedUrl])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) audio.pause()
    else audio.play()

    setIsPlaying(!isPlaying)
  }

  const seek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(Math.max(0, audioRef.current.currentTime + time), duration)
    }
  }

  const handleSpeedChange = () => {
    const newSpeed = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1
    setSpeed(newSpeed)
    if (audioRef.current) audioRef.current.playbackRate = newSpeed
  }

  const handleSliderChange = (_: Event, value: number | number[]) => {
    if (audioRef.current && typeof value === 'number') {
      audioRef.current.currentTime = value
      setCurrentTime(value)
    }
  }

  const formattedCurrentTime = dayjs.duration(currentTime, 'seconds').format('mm:ss')
  const formattedDuration = dayjs.duration(duration, 'seconds').format('mm:ss')

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: 1,
        boxShadow: 3,
        borderRadius: 2,
      }}
    >
      <Slider min={0} max={duration} value={currentTime} onChange={handleSliderChange} size="small" sx={{ color: 'primary.main' }} />
      <Stack sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }} direction="row">
        <IconButton onClick={() => seek(-10)}>
          <Replay10 />
        </IconButton>
        <IconButton onClick={togglePlay} sx={{ bgcolor: 'purple', color: 'white', borderRadius: '50%' }}>
          {isPlaying ? <Pause /> : <PlayArrow />}
        </IconButton>
        <IconButton onClick={() => seek(10)}>
          <Forward10 />
        </IconButton>
        <Typography>
          {formattedCurrentTime} / {formattedDuration}
        </Typography>
        <Typography sx={{ cursor: 'pointer' }} onClick={handleSpeedChange}>
          {speed}×
        </Typography>
      </Stack>

      <audio ref={audioRef} src={presignedUrl} />
    </Stack>
  )
}
